import { motion } from "framer-motion";
import { FaX } from "react-icons/fa6";
import { 
  FaCheckCircle, 
  FaExclamationCircle, 
  FaInfoCircle, 
  FaRegSadTear, 
  FaExclamationTriangle 
} from "react-icons/fa";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { arClass } from "../utils/useArClass";
import success from '../assets/alerts/success.mp3'
import error from '../assets/alerts/error.mp3'
import info from '../assets/alerts/info.mp3'
import warning from '../assets/alerts/warning.mp3';
import { get } from "lodash";

// Create audio objects for each type
const audioFiles = {
  success,
  error,
  info,
  warning
};

const AlertBox = ({ 
  alert, 
  index, 
  darkMode, 
  alertDuration = 4000, 
  onClose 
}) => {
  const defaultLanguage = useSelector((state) => state.settings.defaultLanguage);
  const isRTL = defaultLanguage === "ar";
  const [hasPlayedSound, setHasPlayedSound] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [remainingTime, setRemainingTime] = useState(alertDuration);
  
  const audioCache = useRef({});
  const timeoutRef = useRef(null);
  const progressBarRef = useRef(null);
  const startTimeRef = useRef(Date.now());

  // Preload audio files
  useEffect(() => {
    Object.entries(audioFiles).forEach(([type, url]) => {
      if (!audioCache.current[type]) {
        try {
          const audio = new Audio(url);
          audio.preload = 'auto';
          audio.volume = 0.2;
          audioCache.current[type] = audio;
        } catch (error) {
          // Handle error silently
        }
      }
    });
  }, []);

  // Play sound when alert appears
  useEffect(() => {
    if (!hasPlayedSound && alert.type && audioCache.current[alert.type]) {
      const playSound = async () => {
        try {
          const audio = audioCache.current[alert.type];
          
          // Reset audio to beginning if it's already playing
          audio.currentTime = 0;
          
          // Play the sound
          await audio.play();
          setHasPlayedSound(true);
          
          // Handle audio end
          audio.onended = () => {
            audio.currentTime = 0;
          };
        } catch (error) {
          // Handle error silently
        }
      };
      
      playSound();
    }
  }, [alert.type, hasPlayedSound]);

  // Set up auto-dismiss timer with rollback animation
  useEffect(() => {
    if (alert.type !== "confirmation") {
      // Reset start time
      startTimeRef.current = Date.now();
      
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout
      timeoutRef.current = setTimeout(() => {
        // Trigger rollback animation
        setIsExiting(true);
        
        // Wait for animation to complete before calling onClose
        setTimeout(() => {
          onClose();
        }, 300); // Match this with animation duration
      }, alertDuration);

      // Cleanup
      return () => {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
      };
    }
  }, [alertDuration, alert.type, onClose]);

  // Pause timer on hover, resume on leave
  const handleMouseEnter = () => {
    if (!isExiting) {
      setIsHovered(true);
      
      // Calculate elapsed time
      const elapsed = Date.now() - startTimeRef.current;
      const remaining = alertDuration - elapsed;
      setRemainingTime(remaining > 0 ? remaining : 0);
      
      // Clear the current timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isExiting && isHovered) {
      setIsHovered(false);
      
      // Resume timer with remaining time
      if (remainingTime > 0) {
        startTimeRef.current = Date.now();
        
        timeoutRef.current = setTimeout(() => {
          setIsExiting(true);
          setTimeout(() => {
            onClose();
          }, 300);
        }, remainingTime);
      }
    }
  };

  const handleManualClose = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Trigger rollback animation
    setIsExiting(true);
    
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300); // Match this with animation duration
  };

  const getIcon = (type) => {
    const iconClass = "w-5 h-5 flex-shrink-0";
    switch (type) {
      case "success":
        return <FaCheckCircle className={`${iconClass} text-emerald-500`} />;
      case "error":
        return <FaRegSadTear className={`${iconClass} text-rose-500`} />;
      case "info":
        return <FaInfoCircle className={`${iconClass} text-blue-500`} />;
      case "warning":
        return <FaExclamationTriangle className={`${iconClass} text-amber-500`} />;
      default:
        return <FaInfoCircle className={`${iconClass} ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />;
    }
  };

  const getAlertStyles = (type,useBaseStyles=true) => {
    const baseStyles = isRTL  ? "rounded-xl border-r-4 shadow-lg backdrop-blur-sm" : "rounded-xl border-l-4 shadow-lg backdrop-blur-sm";
    
    if (darkMode) {
      switch (type) {
        case "success":
          return `${useBaseStyles ? baseStyles : ""} bg-emerald-950/90 border-emerald-500 text-emerald-100`;
        case "error":
          return `${useBaseStyles ? baseStyles : ""} bg-rose-950/90 border-rose-500 text-rose-100`;
        case "info":
          return `${useBaseStyles ? baseStyles : ""} bg-blue-950/90 border-blue-500 text-blue-100`;
        case "warning":
          return `${useBaseStyles ? baseStyles : ""} bg-amber-950/90 border-amber-500 text-amber-100`;
        default:
          return `${useBaseStyles ? baseStyles : ""} bg-gray-800/90 border-gray-500 text-gray-100`;
      }
    } else {
      switch (type) {
        case "success":
          return `${useBaseStyles ? baseStyles : ""} bg-emerald-50 border-emerald-500 text-emerald-800`;
        case "error":
          return `${useBaseStyles ? baseStyles : ""} bg-rose-50 border-rose-500 text-rose-800`;
        case "info":
          return `${useBaseStyles ? baseStyles : ""} bg-blue-50 border-blue-500 text-blue-800`;
        case "warning":
          return `${useBaseStyles ? baseStyles : ""} bg-amber-50 border-amber-500 text-amber-800`;
        default:
          return `${useBaseStyles ? baseStyles : ""} bg-gray-50 border-gray-500 text-gray-800`;
      }
    }
  };

  const getProgressBarColor = (type) => {
    switch (type) {
      case "success":
        return "bg-emerald-500";
      case "error":
        return "bg-rose-500";
      case "info":
        return "bg-blue-500";
      case "warning":
        return "bg-amber-500";
      default:
        return darkMode ? "bg-gray-500" : "bg-gray-400";
    }
  };

  // Animation variants for rollback effect
  const rollbackVariants = {
    initial: { opacity: 0, x: isRTL ? -300 : 300, scale: 0.9 },
    animate: { opacity: 1, x: 0, scale: 1 },
    exit: {
      opacity: 0,
      x: isRTL ? -300 : 300,
      scale: 0.9,
      transition: {
        type: "spring",
        damping: 25,
        stiffness: 300,
        duration: 0.3
      }
    }
  }; 

  // Calculate progress bar width based on hover state
  const getProgressBarWidth = () => {
    if (isExiting) return "100%";
    if (isHovered) {
      // Keep progress bar at current width when hovered
      const progressPercent = ((alertDuration - remainingTime) / alertDuration) * 100;
      return `${Math.min(progressPercent, 100)}%`;
    }
    return "0%";
  };


  
  return (
    <motion.div
      key={alert.id}
      variants={rollbackVariants}
      initial="initial"
      animate={isExiting ? "exit" : "animate"}
      exit="exit"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={`relative overflow-hidden ${getAlertStyles(alert.type)} group`}
      style={{ zIndex: 10000 - index }}
    >
      {/* Progress Bar */}
      <motion.div
        ref={progressBarRef}
        className={`${isRTL ? 'absolute top-0 right-0 h-1' : 'absolute top-0 left-0 h-1'} ${getProgressBarColor(alert.type)}`}
        initial={{ width: "100%" }}
        animate={{ 
          width: getProgressBarWidth(),
          transition: { 
            duration: isHovered ? 0 : alertDuration / 1000,
            ease: "linear"
          }
        }}
      />
      
      <div className={arClass("p-4")}>
        <div className={arClass("flex items-start gap-3")}>
          {/* Icon */}
          <div className={arClass("mt-0.5")}>
            {getIcon(alert.type)}
          </div>
          
          {/* Content */}
          <div className={arClass("flex-1 min-w-0")}>
            <div className={arClass("flex items-start justify-between gap-2")}>
              <h3 className={`font-semibold text-sm leading-tight ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                {alert.title}
              </h3>
              <button
                onClick={handleManualClose}
                className={`flex-shrink-0 p-1 rounded-full transition-all duration-200 z-[10000]  ${getAlertStyles(alert.type)} border !border-r !border-f`}
              >
                <FaX className="w-3 h-3" />
              </button>
            </div>
            <p className={arClass(`mt-1 text-sm leading-relaxed ${
              darkMode ? 'text-gray-300' : 'text-gray-600'
            }`)}>
              {alert.message}
            </p>
          </div>
        </div>
      </div>

      {/* Hover effect background */}
      <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 ${
        darkMode ? 'bg-white/5' : 'bg-black/5'
      }`} />
    </motion.div>
  );
};

export default AlertBox;